import { useState } from 'react';
import { AlertCircle, Download, Loader2 } from 'lucide-react';

import myShopClient from '@services/nearlive';
import { SoftCard } from '@/components/ui/soft-card';

const SUPPORT_EMAIL = 'support@unify.near.st';

/**
 * Self-serve fix hints keyed by GMC `itemLevelIssue.code`. Kept short and
 * direct — the retailer should know in one line where to go and what to
 * change. Unknown codes fall back to the support line.
 *
 * Extend rather than refactor: GMC returns long-tail codes — add an entry
 * here as the case becomes clear from real shop data.
 */
const HINTS = {
	missing_gtin: 'Add the product’s barcode in Manage products.',
	gtin_invalid: 'Double-check the barcode digits in Manage products.',
	gtin_not_unique: 'Two products share a barcode — give each its own GTIN.',
	image_link_invalid: 'Replace the broken image URL.',
	image_link_broken: 'Image link returned an error — replace it.',
	missing_image_link: 'Add a product image in Manage products.',
	description_too_short: 'Make the description longer.',
	missing_description: 'Add a description in Manage products.',
	title_missing: 'Add a product title.',
	title_too_short: 'Make the title more descriptive.',
	price_mismatch: "Your website price doesn't match the feed - keep them in sync.",
	availability_mismatch: 'Stock status disagrees with the feed — check the product isn’t hidden.',
	landing_page_not_crawlable: 'Google can’t crawl your shop page — check it’s publicly reachable.',
	landing_page_error: 'The product page is returning an error — make sure it loads cleanly.'
};

const GENERIC_HINT = (
	<>
		Email{' '}
		<a href={`mailto:${SUPPORT_EMAIL}`} className="font-semibold text-foreground hover:underline">
			{SUPPORT_EMAIL}
		</a>{' '}
		with the affected products and we’ll walk through it.
	</>
);

/**
 * Fetches the CSV with the Clerk Bearer token attached, then synthesises a
 * browser download. A plain `<a href>` to the same endpoint sees no auth —
 * the browser doesn't add Authorization headers on anchor navigations — so
 * the apiAdaptor returns "no permissions" even for permitted users.
 */
async function downloadCsv({ shopId, code, filename }) {
	const client = await myShopClient();
	const response = await client.get(`/api/channels/${shopId}/google/disapprovals.csv`, {
		params: { code },
		responseType: 'blob'
	});
	const url = URL.createObjectURL(response.data);
	try {
		const a = document.createElement('a');
		a.href = url;
		a.download = filename;
		document.body.appendChild(a);
		a.click();
		a.remove();
	} finally {
		URL.revokeObjectURL(url);
	}
}

/**
 * Per-reason breakdown of why Google has disapproved products, cached on the
 * Google channel doc by updateGMCStats's daily cron. Each row carries a
 * concise self-serve hint and a "Download" affordance that hits GMC live
 * for the per-product barcode list filtered to that issue code, so the
 * retailer can bulk-fix in their POS without contacting support.
 *
 * Renders nothing when there are no disapproval reasons or every count is
 * zero, so the section header above it can also short-circuit cleanly.
 */
export function GmcIssuesCard({ disapprovedByReason, shopId }) {
	const [busyCode, setBusyCode] = useState(null);
	const [errorCode, setErrorCode] = useState(null);

	if (!Array.isArray(disapprovedByReason) || disapprovedByReason.length === 0) return null;

	const total = disapprovedByReason.reduce((sum, r) => sum + (Number(r.productCount) || 0), 0);
	if (total === 0) return null;

	const handleDownload = async (code) => {
		setBusyCode(code);
		setErrorCode(null);
		try {
			await downloadCsv({ shopId, code, filename: `google-disapprovals-${code}-${shopId}.csv` });
		} catch (err) {
			setErrorCode(code);
		} finally {
			setBusyCode(null);
		}
	};

	return (
		<SoftCard className="!p-0">
			<ul className="divide-y divide-border/60">
				{disapprovedByReason.map((reason) => {
					const hint = HINTS[reason.code];
					const isBusy = busyCode === reason.code;
					const hasError = errorCode === reason.code;
					return (
						<li key={reason.code} className="flex items-start gap-4 px-5 py-4 md:px-6 md:py-5">
							<div className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center">
								<AlertCircle className="h-5 w-5 text-warning-soft-foreground" />
							</div>
							<div className="min-w-0 flex-1">
								<div className="text-sm font-semibold text-foreground">{reason.description || reason.code}</div>
								<p className="mt-0.5 text-xs text-muted-foreground">{hint || GENERIC_HINT}</p>
								{hasError ? (
									<p className="mt-1 text-xs text-destructive">Couldn’t fetch the list — try again in a moment.</p>
								) : null}
							</div>
							<div className="flex shrink-0 items-center gap-3">
								<span className="text-sm font-bold tabular-nums text-foreground">
									{(Number(reason.productCount) || 0).toLocaleString()}
								</span>
								{shopId ? (
									<button
										type="button"
										onClick={() => handleDownload(reason.code)}
										disabled={isBusy}
										className="inline-flex items-center gap-1.5 rounded-full bg-muted px-3 py-1.5 text-xs font-semibold text-foreground transition hover:bg-accent disabled:cursor-wait disabled:opacity-70"
									>
										Download {isBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Download className="h-3 w-3" />}
									</button>
								) : null}
							</div>
						</li>
					);
				})}
			</ul>
		</SoftCard>
	);
}
